import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider, isFirebaseConfigured } from './firebaseClient';
import logger from './loggerService';

export interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role?: string;
  avatarUrl?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName?: string;
  lastName?: string;
  email: string;
  password: string;
  phone?: string;
}

const AUTH_TOKEN_KEY = 'authToken';
const USER_KEY = 'user';
const REFRESH_TOKEN_KEY = 'refreshToken'; 

const requireFirebaseConfig = () => {
  if (!isFirebaseConfigured) {
    throw new Error('Firebase credentials are missing. Add them to your environment variables.');
  }
};

const getProfile = async (userId: string): Promise<User | null> => {
  requireFirebaseConfig();
  try {
    const docRef = doc(db, 'profiles', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: userId,
        firstName: data.first_name || data.firstName,
        lastName: data.last_name || data.lastName,
        avatarUrl: data.avatar_url || data.avatarUrl,
        email: data.email || '',
        role: data.role || 'parent'
      };
    }
  } catch (error) {
    logger.error('Error fetching profile from Firestore', { userId }, error as Error);
  }
  return null;
};

const saveProfile = async (userId: string, data: Partial<User> & { is_super_admin?: boolean }) => {
  const docRef = doc(db, 'profiles', userId);
  await setDoc(docRef, {
    first_name: data.firstName || '',
    last_name: data.lastName || '',
    email: data.email || '',
    avatar_url: data.avatarUrl || '',
    role: data.role || 'parent',
    is_super_admin: data.is_super_admin !== undefined ? data.is_super_admin : false,
    updated_at: new Date().toISOString()
  }, { merge: true });
};

const authService = {
  getProfile,
  
  // Dummy supabase obj to bridge to Firebase Auth natively
  supabase: {
     auth: {
       signOut: () => signOut(auth),
       getSession: async () => {
         const u = auth.currentUser;
         if (!u) return { data: { session: null }, error: null };
         const t = await u.getIdToken();
         const dummySession = { 
           access_token: t, 
           refresh_token: t, 
           user: { 
             id: u.uid, 
             email: u.email, 
             role: 'parent',
             user_metadata: { first_name: u.displayName || undefined, last_name: '', avatar_url: u.photoURL || undefined } 
           } 
         };
         return { data: { session: dummySession }, error: null };
       },
       getUser: async () => {
         const u = auth.currentUser;
         if (!u) return { data: { user: null }, error: null };
         const dummyUser = { 
           id: u.uid, 
           email: u.email, 
           role: 'parent',
           user_metadata: { first_name: u.displayName || undefined, last_name: '', avatar_url: u.photoURL || undefined } 
         };
         return { data: { user: dummyUser }, error: null };
       },
       onAuthStateChange: (callback: any) => {
         const unsubscribe = onAuthStateChanged(auth, async (u) => {
            if (u) {
              const dummySession = { 
                access_token: await u.getIdToken(), 
                refresh_token: await u.getIdToken(), 
                user: { 
                  id: u.uid, 
                  email: u.email, 
                  role: 'parent',
                  user_metadata: { first_name: u.displayName || undefined, last_name: '', avatar_url: u.photoURL || undefined } 
                } 
              };
              callback('SIGNED_IN', dummySession);
            } else {
              callback('SIGNED_OUT', null);
            }
         });
         return { data: { subscription: { unsubscribe } } };
       }
     }
  },

  async login(credentials: LoginCredentials): Promise<User> {
    requireFirebaseConfig();
    const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
    const u = userCredential.user;
    let profile = await getProfile(u.uid);

    if (!profile) {
      const fallbackName = u.displayName?.split(' ') || [];
      const isAdmin = u.email === 'badolahemal@gmail.com';
      profile = {
        id: u.uid,
        email: u.email || '',
        firstName: fallbackName[0] || '',
        lastName: fallbackName.slice(1).join(' ') || '',
        avatarUrl: u.photoURL || undefined,
        role: isAdmin ? 'admin' : 'parent'
      };
      await saveProfile(u.uid, { ...profile, is_super_admin: isAdmin });
    } else if (u.email === 'badolahemal@gmail.com' && profile.role !== 'admin') {
      profile.role = 'admin';
      await saveProfile(u.uid, { role: 'admin', is_super_admin: true });
    }

    const fullUser: User = {
      id: u.uid,
      email: u.email || '',
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      avatarUrl: profile?.avatarUrl,
      role: profile?.role,
    };

    const token = await u.getIdToken();
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(fullUser));
    return fullUser;
  },

  async register(userData: RegisterData): Promise<{ user: User | null; error: any }> {
    requireFirebaseConfig();
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
      const u = userCredential.user;
      const newUser: User = {
        id: u.uid,
        email: u.email || '',
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: 'parent'
      };
      await saveProfile(u.uid, newUser);
      return { user: newUser, error: null };
    } catch (error: any) {
      return { user: null, error };
    }
  },

  async logout(): Promise<void> {
    requireFirebaseConfig();
    await signOut(auth);
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.location.href = '/login';
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem(AUTH_TOKEN_KEY) || !!auth.currentUser;
  },
  getCurrentUser(): User | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try { return JSON.parse(raw) as User; } catch { return null; }
  },
  getToken(): string | null { return localStorage.getItem(AUTH_TOKEN_KEY); },
  getRefreshToken(): string | null { return localStorage.getItem(REFRESH_TOKEN_KEY); },

  async refreshToken(): Promise<string | null> {
    requireFirebaseConfig();
    const u = auth.currentUser;
    if (!u) return null;
    try {
      const token = await u.getIdToken(true);
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      return token;
    } catch (err) {
      return null;
    }
  },

  async signInWithProvider(providerInfo: any) {
    requireFirebaseConfig();
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const u = result.user;
      let profile = await getProfile(u.uid);
      const isAdmin = u.email === 'badolahemal@gmail.com';
      
      if (!profile) {
          const fallbackName = u.displayName?.split(' ') || [];
          profile = {
            id: u.uid,
            email: u.email || '',
            firstName: fallbackName[0] || '',
            lastName: fallbackName.slice(1).join(' ') || '',
            avatarUrl: u.photoURL || undefined,
            role: isAdmin ? 'admin' : 'parent'
          };
          await saveProfile(u.uid, { ...profile, is_super_admin: isAdmin });
      } else if (isAdmin && profile.role !== 'admin') {
          profile.role = 'admin';
          await saveProfile(u.uid, { role: 'admin', is_super_admin: true });
      }
      const token = await u.getIdToken();
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      const fullUser: User = {
        id: u.uid,
        email: u.email || '',
        firstName: profile.firstName,
        lastName: profile.lastName,
        avatarUrl: profile.avatarUrl,
        role: profile.role
      };
      localStorage.setItem(USER_KEY, JSON.stringify(fullUser));
      window.dispatchEvent(new Event('authChange'));
      return { data: { user: u, session: { access_token: token } }, error: null };
    } catch (error: any) {
      throw new Error(error.message);
    }
  },
  
  syncSupabaseSession: async (session: any) => { return null; },
  handleTokenRefresh: (session: any) => {}
};

export default authService;
